import {
  CheckCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";

import api from "@/utils/api";
import http from "@/utils/http";

const { Text } = Typography;
const DATE_FORMAT = "MM/DD/YYYY";

const getDefaultVoidRemark = (date) =>
  `Void Check Dated${date ? ` ${date.format(DATE_FORMAT)}` : ""}`;

const money = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

const compactParams = (values) =>
  Object.fromEntries(
    Object.entries(values).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  );

const VoidCheckPanel = ({ onOperationSuccess }) => {
  const [searchForm] = Form.useForm();
  const [settingForm] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [operation, setOperation] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [trustOptions, setTrustOptions] = useState([]);
  const [trustLoading, setTrustLoading] = useState(false);
  const [bankOptions, setBankOptions] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [tableHeight, setTableHeight] = useState(320);
  const tableHostRef = useRef(null);
  const vendorTimerRef = useRef(null);
  const lastSearchRef = useRef(null);
  const remarkEditedRef = useRef(false);

  useEffect(() => {
    const voidCheckDate = dayjs();
    settingForm.setFieldsValue({
      voidCheckDate,
      voidCheckRemark: getDefaultVoidRemark(voidCheckDate),
      postTo900Before: dayjs().startOf("year"),
    });
    return () => clearTimeout(vendorTimerRef.current);
  }, [settingForm]);

  // Keep the table body fitted to the result card, following OneLineCashFlow.
  useEffect(() => {
    const host = tableHostRef.current;
    if (!host) return undefined;
    const resize = () => {
      const headerHeight =
        host.querySelector(".ant-table-header")?.offsetHeight || 55;
      setTableHeight(Math.max(180, host.clientHeight - headerHeight - 8));
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    resize();
    return () => observer.disconnect();
  }, [rows]);

  const loadVendors = async (content = "") => {
    setVendorLoading(true);
    try {
      const response = await http.get(api.vendorSearch, { params: { content } });
      if (response?.code !== "1") return;
      setVendorOptions(
        (response.data || []).map((item) => ({
          value: item.vendorId,
          label: [item.vendorId, item.vendorName].filter(Boolean).join(" - "),
        })),
      );
    } catch (_error) {
      message.warning("Vendor options could not be loaded.");
    } finally {
      setVendorLoading(false);
    }
  };

  const searchVendors = (content) => {
    clearTimeout(vendorTimerRef.current);
    vendorTimerRef.current = setTimeout(() => loadVendors(content), 300);
  };

  const loadTrusts = async () => {
    if (trustOptions.length) return;
    setTrustLoading(true);
    try {
      const response = await http.get(api.getTrustDictList);
      setTrustOptions(
        (response?.data || []).map((item) => ({
          value: item.trustId,
          label: item.trustNameTitle || item.trust,
        })),
      );
    } catch (_error) {
      message.warning("Trust options could not be loaded.");
    } finally {
      setTrustLoading(false);
    }
  };

  const loadBanks = async () => {
    if (bankOptions.length) return;
    setBankLoading(true);
    try {
      const response = await http.get(api.getBackDictList);
      setBankOptions(
        (response?.data || []).map((item) => ({
          value: item.bankId,
          label: [item.bankName || item.name, item.bank || item.cpAcct]
            .filter(Boolean)
            .join(" - "),
        })),
      );
    } catch (_error) {
      message.warning("Bank options could not be loaded.");
    } finally {
      setBankLoading(false);
    }
  };

  const runSearch = async (values) => {
    const params = compactParams({
      checkDate: values.checkDate?.format("YYYY-MM-DD"),
      fromCheckNo: values.fromCheckNo?.trim(),
      toCheckNo: values.toCheckNo?.trim(),
      fromBatch: values.fromBatch,
      toBatch: values.toBatch,
      vendorCode: values.vendorCode,
      trustId: values.trustId,
      // The Java request object currently exposes the bank field as banId.
      banId: values.bankId,
    });

    setSearchLoading(true);
    setHasSearched(true);
    try {
      const response = await http.get(api.getShowGlTrans, { params });
      if (response?.code !== "1") {
        message.error(response?.msg || "Checks could not be loaded.");
        return;
      }
      const nextRows = (response.data || []).map((item) => ({
        ...item,
        key: item.seq,
      }));
      setRows(nextRows);
      setSelectedRowKeys([]);
      lastSearchRef.current = values;
    } catch (_error) {
      message.error("Checks could not be loaded.");
    } finally {
      setSearchLoading(false);
    }
  };

  const resetSearch = () => {
    searchForm.resetFields();
    setRows([]);
    setSelectedRowKeys([]);
    setHasSearched(false);
    lastSearchRef.current = null;
  };

  const selectedRows = useMemo(() => {
    const selected = new Set(selectedRowKeys);
    return rows.filter((item) => selected.has(item.seq));
  }, [rows, selectedRowKeys]);

  const handleSettingValuesChange = (changedValues) => {
    if (Object.prototype.hasOwnProperty.call(changedValues, "voidCheckRemark")) {
      remarkEditedRef.current = true;
      return;
    }
    if (
      Object.prototype.hasOwnProperty.call(changedValues, "voidCheckDate") &&
      !remarkEditedRef.current
    ) {
      settingForm.setFieldValue(
        "voidCheckRemark",
        getDefaultVoidRemark(changedValues.voidCheckDate),
      );
    }
  };

  const submitOperation = async (type) => {
    if (!selectedRowKeys.length) {
      message.warning("Select at least one check.");
      return;
    }

    let values;
    try {
      values = await settingForm.validateFields();
    } catch (_validationError) {
      return;
    }

    const isReissue = type === "reissue";
    Modal.confirm({
      title: isReissue ? "Void and re-issue selected checks?" : "Void selected checks?",
      content: isReissue
        ? `${selectedRowKeys.length} selected check(s) will be voided and recreated as pending checks.`
        : `${selectedRowKeys.length} selected check(s) will be voided. This action cannot be undone from this screen.`,
      okText: isReissue ? "Void & Re-issue" : "Void Check",
      okButtonProps: { danger: !isReissue },
      onOk: async () => {
        setOperation(type);
        try {
          // Spring binds Set<Integer> only when seqSet is sent as repeated keys.
          const params = new URLSearchParams();
          selectedRowKeys.forEach((seq) => params.append("seqSet", seq));
          params.set("voidCheckDate", values.voidCheckDate.format("YYYY-MM-DD"));
          params.set("voidCheckRemark", values.voidCheckRemark.trim());
          params.set(
            "postTo900Before",
            values.postTo900Before.format("YYYY-MM-DD"),
          );
          const response = await http.get(
            isReissue ? api.getVoidCheckReissue : api.getVoidCheckSubmit,
            { params },
          );
          if (response?.code !== "1") {
            message.error(response?.msg || "The operation failed.");
            return;
          }
          message.success(
            response?.msg ||
              (isReissue
                ? "Checks were voided and re-issued."
                : "Checks were voided."),
          );
          setSelectedRowKeys([]);
          onOperationSuccess?.();
          if (lastSearchRef.current) await runSearch(lastSearchRef.current);
        } catch (error) {
          message.error(
            error?.response?.data?.msg ||
              error?.message ||
              "The operation failed.",
          );
        } finally {
          setOperation("");
        }
      },
    });
  };

  const columns = [
    {
      title: "Check #",
      dataIndex: "checkNumber",
      width: 105,
      fixed: "left",
      render: (value) => <span className="void-check-code">{value || "-"}</span>,
    },
    { title: "Check Date", dataIndex: "checkDate", width: 112 },
    {
      title: "Amount",
      dataIndex: "checkAmount",
      width: 118,
      align: "right",
      render: (value) => <span className="void-check-money">{money(value)}</span>,
    },
    {
      title: "Eligibility",
      key: "eligibility",
      width: 180,
      render: (_, record) => {
        if (record.voidedBefore) {
          return (
            <div className="void-check-eligibility">
              <Tag color="orange">Voided</Tag>
              {record.voidDate && <span>{record.voidDate}</span>}
            </div>
          );
        }
        if (record.clearedDate) {
          return (
            <div className="void-check-eligibility">
              <Tag color="red">Cleared</Tag>
              <span>{record.clearedDate}</span>
            </div>
          );
        }
        return <Tag color="green">Ready</Tag>;
      },
    },
    { title: "Property", dataIndex: "prop", width: 100 },
    { title: "GL Account", dataIndex: "glTitle", width: 200 },
    { title: "Vendor", dataIndex: "vendor", width: 190 },
    { title: "Module", dataIndex: "module", width: 135 },
    { title: "Bank", dataIndex: "bankName", width: 165 },
    { title: "Bank Account", dataIndex: "bankAccount", width: 145 },
    { title: "Trust", dataIndex: "trustName", width: 170 },
    { title: "System Bank #", dataIndex: "systemBankNumber", width: 125 },
    { title: "Batch #", dataIndex: "batchNumber", width: 105 },
    { title: "Printed By", dataIndex: "printBy", width: 145 },
  ];

  const selectedAmount = selectedRows.reduce(
    (total, item) => total + Number(item.checkAmount || 0),
    0,
  );

  return (
    <section className="void-check-workspace">
      <Card
        className="void-check-filter-card"
        title={
          <div className="void-check-filter-title">
            <span>Search and settings</span>
            <Button type="text" icon={<ReloadOutlined />} onClick={resetSearch}>
              Reset
            </Button>
          </div>
        }
      >
        <Form form={searchForm} layout="vertical" onFinish={runSearch}>
          <Form.Item
            label="Check Date"
            name="checkDate"
            rules={[{ required: true, message: "Select a check date." }]}
          >
            <DatePicker format={DATE_FORMAT} style={{ width: "100%" }} />
          </Form.Item>

          <div className="void-check-paired-fields">
            <Form.Item label="From Check #" name="fromCheckNo">
              <Input allowClear inputMode="numeric" />
            </Form.Item>
            <Form.Item label="To Check #" name="toCheckNo">
              <Input allowClear inputMode="numeric" />
            </Form.Item>
          </div>

          <div className="void-check-paired-fields">
            <Form.Item label="From Batch" name="fromBatch">
              <InputNumber min={0} precision={0} stringMode controls={false} />
            </Form.Item>
            <Form.Item label="To Batch" name="toBatch">
              <InputNumber min={0} precision={0} stringMode controls={false} />
            </Form.Item>
          </div>

          <Form.Item label="Vendor" name="vendorCode">
            <Select
              allowClear
              showSearch
              filterOption={false}
              loading={vendorLoading}
              options={vendorOptions}
              placeholder="Search vendor"
              onSearch={searchVendors}
              onDropdownVisibleChange={(open) => open && loadVendors("")}
            />
          </Form.Item>

          <Form.Item label="Trust" name="trustId">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              loading={trustLoading}
              options={trustOptions}
              placeholder="Select trust"
              onDropdownVisibleChange={(open) => open && loadTrusts()}
            />
          </Form.Item>

          <Form.Item label="Bank" name="bankId">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              loading={bankLoading}
              options={bankOptions}
              placeholder="Select bank"
              onDropdownVisibleChange={(open) => open && loadBanks()}
            />
          </Form.Item>

          <Button
            block
            type="primary"
            htmlType="submit"
            icon={<SearchOutlined />}
            loading={searchLoading}
          >
            Search Checks
          </Button>
        </Form>

        <Divider />

        <div className="void-check-setting-heading">
          <Text strong>Void check settings</Text>
          <Text type="secondary">Applied to every selected check.</Text>
        </div>
        <Form
          form={settingForm}
          layout="vertical"
          onValuesChange={handleSettingValuesChange}
        >
          <Form.Item
            label="Void Check Date"
            name="voidCheckDate"
            rules={[{ required: true, message: "Select a void check date." }]}
          >
            <DatePicker format={DATE_FORMAT} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Void Check Remark"
            name="voidCheckRemark"
            rules={[
              { required: true, whitespace: true, message: "Enter a remark." },
            ]}
          >
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
          <Form.Item
            label="Post to 900 if Check Date is before"
            name="postTo900Before"
            rules={[{ required: true, message: "Select the 900 cutoff date." }]}
          >
            <DatePicker format={DATE_FORMAT} style={{ width: "100%" }} />
          </Form.Item>
        </Form>

        <div className="void-check-operation-buttons">
          <Button
            danger
            icon={<StopOutlined />}
            disabled={!selectedRowKeys.length}
            loading={operation === "void"}
            onClick={() => submitOperation("void")}
          >
            Void Check
          </Button>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            disabled={!selectedRowKeys.length}
            loading={operation === "reissue"}
            onClick={() => submitOperation("reissue")}
          >
            Void &amp; Re-issue
          </Button>
        </div>
      </Card>

      <Card
        className="void-check-result-card"
        title="Checks"
        extra={
          <Space size={16}>
            <Text type="secondary">
              {selectedRowKeys.length
                ? `${selectedRowKeys.length} selected · ${money(selectedAmount)}`
                : `${rows.length} result${rows.length === 1 ? "" : "s"}`}
            </Text>
            <Button
              aria-label="Refresh checks"
              icon={<ReloadOutlined />}
              loading={searchLoading}
              disabled={!lastSearchRef.current}
              onClick={() => lastSearchRef.current && runSearch(lastSearchRef.current)}
            />
          </Space>
        }
      >
        <div className="void-check-result-note">
          Cleared and previously voided checks remain visible for review but cannot
          be selected.
        </div>
        <div className="void-check-table-host" ref={tableHostRef}>
          <Table
            rowKey="seq"
            size="small"
            loading={searchLoading}
            columns={columns}
            dataSource={rows}
            pagination={false}
            scroll={{ x: 2065, y: tableHeight }}
            locale={{
              emptyText: hasSearched
                ? "No checks match these filters."
                : "Choose a check date and run the search.",
            }}
            rowClassName={(record) =>
              record.clearedDate || record.voidedBefore
                ? "void-check-row-disabled"
                : ""
            }
            rowSelection={{
              fixed: true,
              columnWidth: 48,
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              getCheckboxProps: (record) => ({
                disabled: Boolean(record.clearedDate || record.voidedBefore),
              }),
            }}
          />
        </div>
      </Card>
    </section>
  );
};

export default VoidCheckPanel;
